import React, { ElementType, useEffect, useMemo, useRef, useState } from 'react';
import RJSFCoreForm from '@rjsf/core';
import Form from '@rjsf/mui';
import { lighten, useTheme } from '@mui/material/styles';
import validator from '@rjsf/validator-ajv8';
import { ThemeProvider } from '@mui/material';
import styled from '@emotion/styled';
import { cleanSchema, getCustomTheme, getUiSchema as getUiSchemaDefault, transformErrors } from './schemaFormUtils';
import ObjectFieldTemplate from './Templates/ObjectFieldTemplate';
import ProgramVersionOutputWidget from './Widgets/ProgramVersionOutputWidget';
import InventoryLink from './Widgets/InventoryLink';
import DateWidget from './Widgets/DateWidget';
import OutputVersionSelectWidget from './Widgets/OutputVersionSelectWidget';
import { getDefaultFormState, RegistryWidgetsType, RJSFSchema, RJSFValidationError, WidgetProps } from '@rjsf/utils';
import AssignedUserWidget from './Widgets/AssignedUserWidget';
import ArrayFieldTemplate from './Templates/ArrayFieldTemplate';
import ArrayFieldItemTemplate from './Templates/ArrayFieldItemTemplate';
import SchemaFormErrorsDialog from './SchemaFormErrorsDialog';
import { setSchemaDialogErrors } from '@/redux/slices/layoutSlice';
import ImageWidget from './Widgets/ImageWidget';
import CheckboxWidget from './Widgets/CheckboxWidget';
import BaseInputTemplate from './Templates/BaseInputTemplate';
import DescriptionFieldTemplate from './Templates/DescriptionFieldTemplate';
import DateTimeUTCWidget from './Widgets/DateTimeUTCWidget';
import { useAppDispatch } from '@/redux/hooks';
import RadioWidget from './Widgets/RadioWidget';
import AutoActionLinkWidget from './Widgets/AutoActionLinkWidget';
import MediaWidget from '@/components/_common/SchemaForm/Widgets/MediaWidget';
import MultiLanguagePickerWidget from '@/components/_common/SchemaForm/Widgets/MultiLanguagePickerWidget';
import AudioWidget from '@/components/_common/SchemaForm/Widgets/AudioWidget';
import SubtitleWidget from '@/components/_common/SchemaForm/Widgets/SubtitleWidget';
import TerritoriesWidget from '@/components/_common/SchemaForm/Widgets/TerritoriesWidget/TerritoriesWidget';
import DatePickerTimezone from '@/components/_common/SchemaForm/Widgets/DatePickerTimezoneWidget/DatePickerTimezoneWidget';
import FileSelectWidget from '@/components/_common/SchemaForm/Widgets/FileSelectWidget';
import TimecodeWidget from '@/components/_common/SchemaForm/Widgets/TimecodeWidget';
import CustomerProfilePickerWidget from './Widgets/CustomerProfilePickerWidget';
import useFunctionOverride from '@/components/_hooks/useFunctionOverride';
import { isEqual } from 'lodash';

// RJSF types: https://github.com/rjsf-team/react-jsonschema-form/blob/main/packages/utils/src/types.ts

export interface SchemaFormProps {
  schema: RJSFSchema;
  formData?: any;
  formRef?: React.RefObject<any>;
  onChange?: (data: any) => void;
  onError?: (errors: any) => void;
  id?: string;
  children?: React.ReactNode;
  formContext?: any;
  showFormTitle?: boolean;
  onSubmit?: (data: any) => void | Promise<any> | undefined;
  displayErrorToast?: boolean;
  tagName?: ElementType;
  readonly?: boolean;
  onRefreshData?: () => void;
  liveOmit?: boolean;
  isInsideModal?: boolean;
  extraData?: any;
}

const seedInitialConditionalArrayItems = (schema: RJSFSchema, data: Record<string, any>) => {
  if (!schema?.properties || Object.keys(data || {}).length > 0) {
    return data;
  }

  const nextData: Record<string, any> = { ...data };

  Object.entries(schema.properties).forEach(([key, prop]: [string, any]) => {
    if (nextData[key] !== undefined) {
      return;
    }

    if (prop?.type !== 'array' || !prop?.items) {
      return;
    }

    const itemSchema = prop.items;
    const discriminator = itemSchema?.properties?.component_type;
    const hasConditionalBranches = !!itemSchema?.if && !!itemSchema?.then;

    if (!hasConditionalBranches || !discriminator) {
      return;
    }

    const defaultComponentType =
      itemSchema?.then?.properties?.component_type?.default ||
      itemSchema?.then?.properties?.component_type?.const ||
      discriminator?.default ||
      discriminator?.enum?.[0];

    if (!defaultComponentType) {
      return;
    }

    nextData[key] = [{ component_type: defaultComponentType }];
  });

  return nextData;
};

const mergeConditionalBranchSchema = (baseSchema: RJSFSchema, branchSchema?: RJSFSchema) => {
  if (!branchSchema || typeof branchSchema !== 'object') {
    return baseSchema;
  }

  const mergedProperties = {
    ...(baseSchema.properties || {}),
    ...(branchSchema.properties || {})
  };
  const mergedRequired = Array.from(new Set([...(baseSchema.required || []), ...(branchSchema.required || [])]));

  return {
    ...baseSchema,
    ...branchSchema,
    properties: mergedProperties,
    ...(mergedRequired.length ? { required: mergedRequired } : {})
  };
};

const resolveConditionalSchemaForData = (schema: RJSFSchema, data: Record<string, any>, rootSchema: RJSFSchema = schema) => {
  let currentSchema: RJSFSchema = schema;

  while (currentSchema?.if && (currentSchema?.then || currentSchema?.else)) {
    const { if: condition, then: thenBranch, else: elseBranch, ...baseSchema } = currentSchema as any;
    const conditionMatches = validator.isValid(condition as RJSFSchema, data || {}, rootSchema);
    const selectedBranch = (conditionMatches ? thenBranch : elseBranch) as RJSFSchema | undefined;

    currentSchema = mergeConditionalBranchSchema(baseSchema as RJSFSchema, selectedBranch);
  }

  return currentSchema;
};

const hydrateConditionalArrayDefaults = (schema: RJSFSchema, data: Record<string, any>, rootSchema: RJSFSchema = schema) => {
  if (!schema?.properties || !data || typeof data !== 'object') {
    return data;
  }

  const nextData: Record<string, any> = { ...data };

  Object.entries(schema.properties).forEach(([key, prop]: [string, any]) => {
    if (nextData[key] === undefined || nextData[key] === null) {
      return;
    }

    if (prop?.type === 'array' && !Array.isArray(prop?.items) && prop?.items?.type === 'object' && Array.isArray(nextData[key])) {
      const itemSchema = prop.items as RJSFSchema;
      nextData[key] = nextData[key].map((item: any) => {
        if (!item || typeof item !== 'object') {
          return item;
        }

        const resolvedItemSchema = resolveConditionalSchemaForData(itemSchema, item, rootSchema);
        return getDefaultFormState(validator, resolvedItemSchema, item, rootSchema);
      });
      return;
    }

    if (prop?.type === 'object' && nextData[key] && typeof nextData[key] === 'object' && !Array.isArray(nextData[key])) {
      nextData[key] = hydrateConditionalArrayDefaults(prop as RJSFSchema, nextData[key], rootSchema);
    }
  });

  return nextData;
};

export const normalizeSchemaFormData = (schema: RJSFSchema, data: Record<string, any>) => {
  const seededData = seedInitialConditionalArrayItems(schema, data || {});
  const hydratedData = hydrateConditionalArrayDefaults(schema, seededData, schema);
  const resolvedSchema = resolveConditionalSchemaForData(schema, hydratedData, schema);
  return getDefaultFormState(validator, resolvedSchema, hydratedData, schema);
};

const SchemaForm = ({
  schema = {},
  formData = {},
  onChange,
  formRef,
  onError,
  id = 'schema-form',
  isInsideModal,
  children,
  formContext,
  showFormTitle = false,
  onSubmit,
  tagName = 'form',
  readonly = false,
  liveOmit = false,
  onRefreshData,
  extraData
}: SchemaFormProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const customTheme = getCustomTheme(theme, showFormTitle);
  const getUiSchema = useFunctionOverride<typeof getUiSchemaDefault>(getUiSchemaDefault, 'getUISchema');
  const uiSchema = getUiSchema(schema);
  const [liveValidate, setLiveValidation] = useState<boolean>(false);
  const internalFormRef = useRef<RJSFCoreForm | null>(null);
  const [widgetLoadingData, updateWidgetLoadingDataState] = useState<boolean>(false);

  const setMultipleRefs = (element: any) => {
    internalFormRef.current = element;
    if (formRef && !Object.isFrozen(formRef)) {
      // Only assign if formRef is not a readonly ref
      (formRef as React.MutableRefObject<any>).current = element;
    }
  };

  const setWidgetLoadingData = (val: boolean) => {
    updateWidgetLoadingDataState(val);

    // false means it was previously true and now it's done loading
    if (!val && onRefreshData) {
      onRefreshData();
    }
  };

  // this is temporary until we remove the required field from the schema
  // requirements are set at top level of schema with an array of field names
  // TODO: if we're going to do this, shouldn't we standardize using it everywhere else in this component instead of schema?
  const formSchema = cleanSchema(schema);

  const normalizedFormData = useMemo(() => {
    try {
      return normalizeSchemaFormData(schema, formData || {});
    } catch (error) {
      console.error('Failed to normalize schema form data with defaults', error);
      return formData;
    }
  }, [schema, formData]);

  let hasTemplates = false;
  if (formSchema.properties) {
    // if there is at least one template in the schema, we need to set the hasTemplates flag to true
    hasTemplates = Object.values(formSchema.properties).some((property: any) => property.templates);
  }

  // these are the custom widgets for `format` types
  const widgets: RegistryWidgetsType = {
    DateWidget: DateWidget,
    DateTimeWidget: (props: WidgetProps) => DateWidget({ ...props, showTimeSelect: true }),
    'date-time': (props: WidgetProps) => DateWidget({ ...props, showTimeSelect: true }),
    VersionOutput: OutputVersionSelectWidget,
    ProgramVersionOutput: (props: WidgetProps) => ProgramVersionOutputWidget({ ...props, formRef }),
    InventoryLink: (props: WidgetProps) => (
      <InventoryLink key={id} formContext={formContext} fileName={props.schema?.rally_label} label={props.label} url={props.value} />
    ),
    AssignedUser: AssignedUserWidget,
    UserPicker: AssignedUserWidget,
    Image: ImageWidget,
    CheckboxWidget: (props: WidgetProps) => <CheckboxWidget {...props} extraData={extraData} formData={normalizedFormData} />,
    DateTimeUTC: DateTimeUTCWidget,
    DateTimeTZ: (props: WidgetProps) => <DatePickerTimezone {...props} extraData={extraData} formData={normalizedFormData} />,
    radio: RadioWidget,
    AutoActionLink: AutoActionLinkWidget,
    MediaResolutionPicker: MediaWidget,
    MultiLanguagePicker: MultiLanguagePickerWidget,
    AudioTrack: AudioWidget,
    TimedTextTracks: SubtitleWidget,
    Territories: TerritoriesWidget,
    FileSelect: FileSelectWidget,
    Timecode: TimecodeWidget,
    CustomerProfilePicker: (props: WidgetProps) => <CustomerProfilePickerWidget {...props} extraData={extraData} />
  };

  const scrollToFirstError = (error: RJSFValidationError) => {
    const formElement: HTMLElement | null = internalFormRef.current?.formElement.current;
    if (formElement && error.property) {
      const id = error.property.replace(/\./g, '');
      const element = formElement.querySelector(`#root_${id}`) || document.getElementById(`root_${id}__title`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleErrors = (errors: RJSFValidationError[]) => {
    if (tagName === 'form') {
      dispatch(setSchemaDialogErrors(errors));
    } else {
      // if it is not a form tagName, liveValidation doesn't just "work", so we have to manually manage it
      setLiveValidation(true);
    }
    if (onError) {
      onError(errors);
    }
    scrollToFirstError(errors[0]);
  };

  const getFormContext = () => {
    // we're hacking this in order to make templates work
    // we are passing in the form's onChange function to the formContext
    // so that the template is able to change the form data
    if (hasTemplates) {
      return {
        onFormChange: (newData: any) => internalFormRef?.current?.onChange({ ...internalFormRef?.current.state?.formData, ...newData }),
        ...formContext,
        isInsideModal,
        widgetLoadingData,
        setWidgetLoadingData,
        formData: normalizedFormData,
        extraData //This is a hack to allow widgets to update when extraData changes
      };
    }
    return {
      ...formContext,
      onFormChange: (newData: any) => internalFormRef?.current?.onChange({ ...internalFormRef?.current.state?.formData, ...newData }),
      isInsideModal,
      widgetLoadingData,
      setWidgetLoadingData,
      formData: normalizedFormData,
      extraData //This is a hack to allow widgets to update when extraData changes
    };
  };

  const handleChange = (data: { formData?: Record<string, any> }) => {
    if (!onChange) return;

    const { formData } = data;
    let nextFormData = formData;

    if (formData) {
      try {
        nextFormData = normalizeSchemaFormData(schema, formData);
      } catch (error) {
        console.error('Failed to normalize changed schema form data with defaults', error);
      }
    }

    if (liveOmit && nextFormData) {
      // Unfortunately rjsf's Form component does not omit fields that don't exist in a schema on the first `onChange` call,
      //  when the form is rendered with the `liveOmit` prop passed to the Form. So we do it manually.
      // This is useful when schema is changed dynamically, and we want to make sure that fields from previous schema are not getting in the form data
      //  when schema is changed.
      const newFormData: Record<string, any> = {};
      Object.keys(schema.properties).forEach((key) => {
        newFormData[key] = nextFormData[key];
      });
      onChange({
        ...data,
        formData: newFormData
      });
    } else {
      onChange({
        ...data,
        formData: nextFormData
      });
    }
  };

  // since normalizedFormData is inserting default values for missing fields, we need to make sure that the parents form data is updated with those default values on initial render and when schema changes
  // also need to gate it to ensure that a formRef has initialized. Finally we make sure there is a delta on normalizedFormData and formData to avoid an infinite loop of updates
  useEffect(() => {
    if (formRef?.current && onChange && !isEqual(normalizedFormData, formData)) {
      onChange({
        ...formRef.current.state,
        formData: normalizedFormData
      });
    }
  }, [formRef?.current, normalizedFormData]);

  return (
    <ThemeProvider theme={customTheme}>
      <SchemaFormErrorsDialog />
      <SchemaFormWrapper
        ref={setMultipleRefs}
        id={id}
        omitExtraData
        liveOmit={liveOmit}
        liveValidate={liveValidate}
        tagName={tagName}
        schema={formSchema || {}}
        formData={normalizedFormData}
        validator={validator}
        uiSchema={uiSchema}
        widgets={widgets}
        onChange={handleChange}
        formContext={getFormContext()}
        onError={handleErrors}
        transformErrors={(errors) => transformErrors(errors, formSchema)}
        onSubmit={onSubmit}
        readonly={readonly || schema.readonly}
        templates={{
          // this prevents the default error list from rendering but will still allow field level errors to render
          ErrorListTemplate: () => null,
          ObjectFieldTemplate,
          ArrayFieldTemplate,
          ArrayFieldItemTemplate,
          BaseInputTemplate,
          DescriptionFieldTemplate
        }}
      >
        {children}
      </SchemaFormWrapper>
    </ThemeProvider>
  );
};

export default SchemaForm;

const SchemaFormWrapper = styled(Form)`
  width: 100%;
  .MuiPaper-root {
    .MuiPaper-root {
      background-color: ${(props) => lighten(props.theme.palette.background.paper, 0.01)};
    }
  }
`;
